<mat-card class="terminal-card">
    <mat-card-header>
        <mat-icon mat-card-avatar class="terminal-avatar">point_of_sale</mat-icon>
        <mat-card-title>{{ terminal.deviceName || 'Payment Terminal' }}</mat-card-title>
        <mat-card-subtitle>
            {{ terminal.config?.providerKind || '—' }}
            @if (driverKind) { · {{ driverKind }} }
        </mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
        <!-- Liveness first: the one thing you look at a column's card to find out. -->
        <div class="terminal-liveness" [class.is-online]="isOnline" [class.is-offline]="!isOnline">
            <mat-icon>{{ isOnline ? 'wifi' : 'wifi_off' }}</mat-icon>
            <span class="liveness-label">{{ isOnline ? 'Online' : 'Offline' }}</span>
            @if (lastHeartbeatAt) {
            <span class="liveness-seen">last seen {{ lastHeartbeatAt | date:'dd.MM.yyyy HH:mm:ss' }}</span>
            } @else {
            <span class="liveness-seen">never seen</span>
            }
        </div>

        <!-- Command reachability, deliberately its own line rather than folded into "Online".
             Both clients at a column share its SignalR group and the kiosk browser beats every 60s,
             so "Online" can be true while nothing reaches the card reader at all — which is exactly
             what this card showed for sixteen hours on 2026-09-02 while eighteen charges vanished.
             Absent when the station has never sent a probe: an older daemon is unknown, not broken,
             and claiming otherwise teaches operators to ignore this line too. -->
        @if (lastCommandAckAt) {
        <div class="terminal-reachability" [class.is-unreachable]="isUnreachableForCommands">
            <mat-icon>{{ isUnreachableForCommands ? 'link_off' : 'link' }}</mat-icon>
            <span class="reachability-label">
                {{ isUnreachableForCommands ? 'Not receiving commands' : 'Receiving commands' }}
            </span>
            <span class="reachability-seen"
                  matTooltip="When a command last reached the card reader. The heartbeat above only shows that something at the column is talking to us — the kiosk screen alone keeps it fresh.">
                confirmed {{ lastCommandAckAt | date:'dd.MM.yyyy HH:mm:ss' }}
            </span>
        </div>
        }

        <!-- The per-station handle: prominent, copyable, runtime (not env). -->
        <div class="terminal-id-block">
            <div class="terminal-id-label">
                <mat-icon>badge</mat-icon>
                <span>Terminal ID (station handle)</span>
            </div>
            <div class="terminal-id-value">
                <code>{{ terminal.id }}</code>
                <button mat-icon-button (click)="copy(terminal.id, 'Terminal ID')" matTooltip="Copy terminal ID"
                    aria-label="Copy terminal ID">
                    <mat-icon>content_copy</mat-icon>
                </button>
            </div>
            <p class="terminal-id-hint">
                The payment app reads this at runtime from its URL — point the station kiosk at
                <code>{{ stationUrl }}</code>. Don't bake it into a build.
                <button mat-button color="primary" class="copy-param-btn"
                    (click)="copy(stationUrl, 'Station URL')">
                    <mat-icon>link</mat-icon> Copy station URL
                </button>
            </p>
        </div>

        <!-- Signing key. The Pi cannot report a payment outcome until its public key is here:
             the outcome endpoint is fail-closed and answers 401, which from the lane looks like
             a card that charged and a barrier that never opened. -->
        <div class="terminal-signing-block">
            <div class="terminal-signing-label">
                <mat-icon>key</mat-icon>
                <span>Signing key</span>
            </div>

            <p class="terminal-signing-hint">
                The station generates its own keypair on first boot and logs the public half. On the Pi:
                <code>sudo grep "signing public key" /var/log/jjames-payment-error.log</code>
                — the <strong>-error</strong> file, the other one stays empty. Paste the base64 value
                (an <code>ssh-ed25519 …</code> line works too). Re-registering rotates the key, and a
                reflashed SD card always needs this again.
            </p>

            <mat-form-field appearance="fill" class="terminal-signing-field">
                <mat-label>Ed25519 public key</mat-label>
                <textarea matInput rows="2"
                          [ngModel]="signingKeyInput()"
                          (ngModelChange)="signingKeyInput.set($event)"
                          [disabled]="isRegisteringKey()"
                          placeholder="base64, or ssh-ed25519 AAAA… comment"></textarea>
            </mat-form-field>

            <div class="terminal-signing-actions">
                <button mat-flat-button color="primary"
                        (click)="registerSigningKey()"
                        [disabled]="!signingKeyInput().trim() || isRegisteringKey() || isProcessing">
                    @if (isRegisteringKey()) {
                    <mat-spinner diameter="18"></mat-spinner>
                    } @else {
                    <mat-icon>vpn_key</mat-icon>
                    }
                    Register key
                </button>
            </div>

            @if (registeredCredential(); as cred) {
            <dl class="terminal-signing-result">
                <div><dt>Algorithm</dt><dd>{{ cred.algorithm || 'Ed25519' }}</dd></div>
                @if (cred.publicKeyFingerprint) {
                <div><dt>Fingerprint</dt><dd><code>{{ cred.publicKeyFingerprint }}</code></dd></div>
                }
                @if (cred.lastRotatedAt) {
                <div><dt>Registered</dt><dd>{{ cred.lastRotatedAt | date:'dd.MM.yyyy HH:mm:ss' }}</dd></div>
                }
            </dl>
            }
        </div>

        @if (terminal.id) {
        <app-terminal-coin-acceptor [terminalId]="terminal.id" [disabled]="isProcessing"></app-terminal-coin-acceptor>
        }

        <dl class="terminal-meta">
            @if (terminal.config?.providerTerminalId) {
            <div><dt>Provider Terminal ID</dt><dd>{{ terminal.config?.providerTerminalId }}</dd></div>
            }
            @if (zvtEndpoint) {
            <div><dt>ZVT reader</dt><dd>{{ zvtEndpoint }}</dd></div>
            }
            @if (terminal.config?.locationCode) {
            <div><dt>Location code</dt><dd>{{ terminal.config?.locationCode }}</dd></div>
            }
        </dl>
    </mat-card-content>

    <mat-card-actions align="end">
        <button mat-stroked-button color="primary" (click)="onEdit()" [disabled]="isProcessing">
            <mat-icon>edit</mat-icon>
            Edit
        </button>
        <button mat-stroked-button color="warn" (click)="onDelete()" [disabled]="isProcessing">
            <mat-icon>delete</mat-icon>
            Delete
        </button>
    </mat-card-actions>
</mat-card>
